// src/components/cart/CartSummary.jsx
import React from 'react';
import { formatCurrency, calculateShipping } from '../../utils/helpers';
import { SHIPPING_CONFIG } from '../../utils/constants';
import Button from '../common/Button';

/**
 * Cart Summary Component
 *
 * Shows:
 * - Subtotal
 * - Shipping charge (free above threshold)
 * - Total amount (prices are GST-inclusive; GST shown as an informational carve-out)
 * - Checkout button
 */
const CartSummary = ({ cart, onCheckout, checkoutLoading = false }) => {
  if (!cart) return null;

  const subtotal = cart.totalAmount || 0;
  const shippingCharge = calculateShipping(
    subtotal,
    SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD,
    SHIPPING_CONFIG.SHIPPING_CHARGE
  );
  // GST is included in the (inclusive) prices — the server computes the exact
  // amount per item, so we display that rather than a hardcoded client rate.
  const gst = cart.tax || 0;
  const total = subtotal + shippingCharge;
  
  const isFreeShipping = subtotal >= SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD;
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>
      
      {/* Item Count */}
      <div className="flex justify-between text-gray-700 mb-3">
        <span>Items ({cart.totalItems})</span>
        <span className="font-medium">{formatCurrency(subtotal)}</span>
      </div>
      
      {/* Shipping */}
      <div className="flex justify-between text-gray-700 mb-3">
        <span>Shipping</span>
        <span className="font-medium">
          {isFreeShipping ? (
            <span className="text-green-600">FREE</span>
          ) : (
            formatCurrency(shippingCharge)
          )}
        </span>
      </div>
      
      {/* Free Shipping Progress */}
      {!isFreeShipping && (
        <div className="mb-4">
          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-green-500 h-full transition-all duration-300"
              style={{
                width: `${Math.min((subtotal / SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD) * 100, 100)}%`,
              }}
            />
          </div>
          <p className="text-xs text-gray-600 mt-1">
            Add {formatCurrency(SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD - subtotal)} more for FREE shipping!
          </p>
        </div>
      )}
      
      <div className="border-t border-gray-300 my-4"></div>

      {/* Total */}
      <div className="flex justify-between text-lg font-bold text-gray-900 mb-1">
        <span>Total</span>
        <span>{formatCurrency(total)}</span>
      </div>
      <p className="text-xs text-gray-500 mb-6">Inclusive of GST {formatCurrency(gst)}</p>
      
      {/* Checkout Button */}
      <Button
        fullWidth
        size="lg"
        onClick={onCheckout}
        loading={checkoutLoading}
      >
        Proceed to Checkout
      </Button>
      
      {/* Security Badge */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-600">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
        <span>Secure Checkout</span>
      </div>
    </div>
  );
};

export default CartSummary;